import { Box, Button, Divider, Text } from '@mantine/core';
import { IconSignature } from '@tabler/icons-react';
import { useState } from 'react';
import type { SignatureInfo } from '../pages/appointments/fhirNoteMapping';
import { SignatureCaptureModal } from './SignatureCapture';
import { SignatureDisplayBox } from './SignatureDisplayBox';

export interface ClientPortalSignatureSectionProps {
  signature: SignatureInfo | null;
  onSignatureChange: (signature: SignatureInfo) => void;
  /** Section heading — defaults to "Client Signature". */
  label?: string;
  /** When false, omit the leading divider (e.g. nested layouts). */
  showDivider?: boolean;
}

/**
 * Controlled client signature block — same visual treatment as note template preview.
 */
export function ClientPortalSignatureSection({
  signature,
  onSignatureChange,
  label = 'Client Signature',
  showDivider = true,
}: ClientPortalSignatureSectionProps): JSX.Element {
  const [signModalOpen, setSignModalOpen] = useState(false);

  return (
    <>
      {showDivider && <Divider />}
      {signature ? (
        <SignatureDisplayBox signature={signature} label={label} />
      ) : (
        <Box
          p="md"
          style={{
            backgroundColor: 'var(--mantine-color-blue-0)',
            borderRadius: 'var(--mantine-radius-md)',
            minHeight: 160,
          }}
        >
          <Text size="sm" c="gray.6" mb={6}>
            {label}
          </Text>
          <Text size="sm" c="gray.5" fs="italic" mb="md">
            Not yet signed
          </Text>
          <Button
            leftSection={<IconSignature size={16} />}
            size="sm"
            onClick={() => setSignModalOpen(true)}
          >
            Sign
          </Button>
        </Box>
      )}
      <SignatureCaptureModal
        opened={signModalOpen}
        onClose={() => setSignModalOpen(false)}
        onSign={(info) => {
          onSignatureChange(info);
          setSignModalOpen(false);
        }}
        title="Sign document"
        submitLabel="Sign"
      />
    </>
  );
}
